<template>
    <div class="page-wrapper">
        <div class="page-container">
            <!-- 面包屑 -->
            <nav class="breadcrumb">
                <a class="bc-link" @click.prevent="goHome">首页</a>
                <span class="bc-sep">/</span>
                <span class="bc-link">{{ config.pageTitle }}</span>
                <span class="bc-sep">/</span>
                <span class="bc-current">处方信息</span>
            </nav>

            <!-- Hero -->
            <div class="hero-row">
                <div class="hero-left">
                    <p class="hero-brand">{{ isChaoweiFlow ? '关盈堂 · 中药调理' : '关盈堂 · 中药AI组方' }}</p>
                    <h1 class="hero-title">细致记录，为辨证提供依据</h1>
                    <p class="hero-subtitle">完善就诊与身体信息，让每一份健康关怀更有针对性。</p>
                </div>
                <button class="hero-back" type="button" @click="goBack">
                    <span class="hero-back-arrow" aria-hidden="true">←</span>
                    <span>返回选择患者</span>
                </button>
            </div>

            <!-- 双栏 -->
            <div class="layout">
                <!-- 左：患者信息 + 步骤选项卡 -->
                <aside class="side-col">
                    <div class="patient-card">
                        <div class="patient-head">
                            <span class="patient-avatar">{{ avatarText }}</span>
                            <div class="patient-meta">
                                <span class="patient-name">{{ consultInfo.contact.name }}</span>
                                <span class="patient-sub">{{ sexText }}<span
                                        v-if="birthdayText"> · {{ birthdayText }}</span></span>
                            </div>
                        </div>
                        <div class="patient-rows">
                            <div v-if="consultInfo.contact.liveArea" class="p-row">
                                <span class="p-k">常住地址</span>
                                <span class="p-v">{{ consultInfo.contact.liveArea }}</span>
                            </div>
                            <div v-if="consultInfo.contact.birthArea" class="p-row">
                                <span class="p-k">出生地址</span>
                                <span class="p-v">{{ consultInfo.contact.birthArea }}</span>
                            </div>
                        </div>
                    </div>

                    <nav class="steps">
                        <button v-for="s in stepList" :key="s.idx" type="button" class="step-item"
                            :class="{ active: currentStep === s.idx }" @click="goStep(s.idx)">
                            <span class="step-idx">{{ pad(s.idx) }}</span>
                            <span class="step-info">
                                <span class="step-name">{{ s.name }}</span>
                                <span class="step-desc">{{ s.desc }}</span>
                            </span>
                            <span class="step-arrow" aria-hidden="true">→</span>
                        </button>
                    </nav>

                    <p class="side-hint">请按实际情况填写，未测量的体征可留空，未知状态无需选择。填写内容不构成诊断或处方。</p>
                </aside>

                <!-- 右：表单 -->
                <section class="form-card">
                    <div class="form-head">
                        <div class="form-head-row">
                            <h2 class="form-title">{{ currentStepName }}</h2>
                            <span class="form-badge">{{ currentStep }}/{{ totalSteps }}</span>
                        </div>
                    </div>
                    <p class="form-sub">{{ currentStepDesc }}</p>

                    <!-- 01 就诊信息 -->
                    <div v-show="currentStep === 1" class="step-body">
                        <div class="grid-2">
                            <div class="field">
                                <label class="f-label">{{ isChaoweiFlow ? '开始调理日期' : '开始服药日期' }}</label>
                                <div class="f-readonly">{{ consultInfo.useDate || '—' }}</div>
                            </div>
                            <div class="field" v-if="config.showGanZhiLabel">
                                <label class="f-label">{{ isChaoweiFlow ? '状况开始日期' : '开始发病日期' }}</label>
                                <div class="f-readonly">{{ consultInfo.illDay || '年/月/日' }}</div>
                            </div>
                            <div class="field" v-if="useDaysValue">
                                <label class="f-label">{{ useDaysLabel }}</label>
                                <div class="f-readonly">{{ useDaysValue }}</div>
                            </div>
                        </div>
                        <div class="grid-2 textarea-grid">
                            <div class="field" :class="{ 'field-full': isChaoweiFlow }" v-if="submitData.basic_enter_array">
                                <label class="f-label">{{ chaoweiFieldLabel(4) || submitData.basic_enter_array[4].key }}</label>
                                <el-input v-model="submitData.basic_enter_array[4].value" type="textarea" :rows="6"
                                    autocomplete="off"
                                    :placeholder="isChaoweiFlow ? '请输入问询结果' : '描述主要不适、持续时间及变化…'" />
                            </div>
                            <div class="field" v-if="!isChaoweiFlow && submitData.basic_enter_array">
                                <label class="f-label">{{ chaoweiFieldLabel(5) || submitData.basic_enter_array[5].key }}</label>
                                <el-input v-model="submitData.basic_enter_array[5].value" type="textarea" :rows="6"
                                    autocomplete="off" placeholder="填写已有医生诊断，无诊断可留空" />
                            </div>
                        </div>
                    </div>

                    <!-- 02 症状信息 -->
                    <div v-show="currentStep === 2" class="step-body">
                        <div class="field field-standalone" v-if="submitData.basic_array">
                            <label class="f-label">{{ submitData.basic_array[0].key }}</label>
                            <el-radio-group v-model="submitData.basic_array[0].value" class="pill-group"
                                :disabled="radioDisable">
                                <el-radio-button v-for="opt in submitData.basic_array[0].options" :key="opt"
                                    :label="opt">{{ opt }}</el-radio-button>
                            </el-radio-group>
                        </div>

                        <div class="grid-2" v-if="submitData.basic_enter_array">
                            <div class="field">
                                <label class="f-label">{{ submitData.basic_enter_array[0].key }}</label>
                                <div class="input-wrap">
                                    <el-input v-model="submitData.basic_enter_array[0].value" autocomplete="off"
                                        placeholder="请填写" />
                                    <span class="unit">℃</span>
                                </div>
                            </div>
                            <div class="field">
                                <label class="f-label">{{ submitData.basic_enter_array[1].key }}</label>
                                <div class="input-wrap">
                                    <el-input v-model="submitData.basic_enter_array[1].value" autocomplete="off"
                                        placeholder="请填写" />
                                    <span class="unit">次/分</span>
                                </div>
                            </div>
                            <div class="field">
                                <label class="f-label">{{ submitData.basic_enter_array[2].key }}</label>
                                <div class="input-wrap">
                                    <el-input v-model="submitData.basic_enter_array[2].value" autocomplete="off"
                                        placeholder="请填写" />
                                    <span class="unit">mmHg</span>
                                </div>
                            </div>
                            <div class="field">
                                <label class="f-label">{{ submitData.basic_enter_array[3].key }}</label>
                                <div class="input-wrap">
                                    <el-input v-model="submitData.basic_enter_array[3].value" autocomplete="off"
                                        placeholder="请填写" />
                                    <span class="unit">mmHg</span>
                                </div>
                            </div>
                        </div>

                        <div class="sub-title">生活状态与脉象</div>
                        <div class="grid-2">
                            <div class="field" v-if="submitData.basic_array">
                                <label class="f-label">{{ submitData.basic_array[1].key }}</label>
                                <el-radio-group v-model="submitData.basic_array[1].value" class="pill-group">
                                    <el-radio-button v-for="opt in submitData.basic_array[1].options" :key="opt"
                                        :label="opt">{{ opt }}</el-radio-button>
                                </el-radio-group>
                            </div>
                            <div class="field" v-if="submitData.basic_array">
                                <label class="f-label">{{ submitData.basic_array[2].key }}</label>
                                <el-radio-group v-model="submitData.basic_array[2].value" class="pill-group">
                                    <el-radio-button v-for="opt in submitData.basic_array[2].options" :key="opt"
                                        :label="opt">{{ opt }}</el-radio-button>
                                </el-radio-group>
                            </div>
                            <div class="field" v-if="submitData.basic_array">
                                <label class="f-label">{{ submitData.basic_array[3].key }}</label>
                                <el-radio-group v-model="submitData.basic_array[3].value" class="pill-group">
                                    <el-radio-button v-for="opt in submitData.basic_array[3].options" :key="opt"
                                        :label="opt">{{ opt }}</el-radio-button>
                                </el-radio-group>
                            </div>
                            <div class="field" v-if="submitData.basic_array">
                                <label class="f-label">{{ submitData.basic_array[4].key }}</label>
                                <el-radio-group v-model="submitData.basic_array[4].value" class="pill-group">
                                    <el-radio-button v-for="opt in submitData.basic_array[4].options" :key="opt"
                                        :label="opt">{{ opt }}</el-radio-button>
                                </el-radio-group>
                            </div>
                            <div class="field" v-for="mx in (submitData.maixiang_array || [])" :key="mx.key">
                                <label class="f-label">{{ mx.key }}</label>
                                <el-radio-group v-model="mx.value" class="pill-group">
                                    <el-radio-button v-for="opt in mx.options" :key="opt" :label="opt">{{ opt }}
                                    </el-radio-button>
                                </el-radio-group>
                            </div>
                        </div>
                    </div>

                    <!-- 03 三焦症状 -->
                    <div v-show="currentStep === 3" class="step-body">
                        <div class="sanjiao-tabs">
                            <button v-for="r in sanjiaoRegions" :key="r.key" type="button" class="sanjiao-tab"
                                :class="{ active: activeSanjiao === r.key }" @click="activeSanjiao = r.key">
                                <span class="sj-name">{{ r.name }}</span>
                                <span class="sj-desc">{{ r.desc }}</span>
                            </button>
                        </div>

                        <div class="sanjiao-groups">
                            <div class="sj-group" v-for="(grp, gi) in activeSanjiaoGroups"
                                :key="activeSanjiao + '-' + grp.key">
                                <div class="sj-group-title">{{ grp.key }}</div>
                                <el-checkbox-group v-model="submitData[activeSanjiao + '_array'][gi].value"
                                    class="chip-group">
                                    <el-checkbox-button v-for="option in grp.options" :label="option" :key="option">
                                        {{ option }}
                                    </el-checkbox-button>
                                </el-checkbox-group>
                            </div>
                        </div>

                        <div class="sanjiao-bar">
                            <span class="bar-count">已选择 {{ selectedSanjiaoCount }} 项症状</span>
                            <button type="button" class="bar-clear" @click="clearSanjiao">清空选择</button>
                        </div>
                    </div>

                    <!-- 底部 -->
                    <div class="form-footer">
                        <span class="form-hint">信息仅在本页面停留期间保留</span>
                        <div class="form-nav">
                            <button v-if="currentStep > 1" type="button" class="btn-prev" @click="prevStep">上一步</button>
                            <button v-if="currentStep < totalSteps" type="button" class="btn-next" @click="nextStep">
                                下一步 <span class="nav-arrow">→</span>
                            </button>
                            <button type="button" class="btn-submit" @click="submit">{{ submitBtnText }}</button>
                        </div>
                    </div>
                </section>
            </div>
        </div>

        <!-- 核对确认弹框 -->
        <el-dialog :visible.sync="confirmVisible" width="940px" :show-close="false" class="cf-dialog">
            <div class="cf">
                <div class="cf-head">
                    <div class="cf-head-left">
                        <h2 class="cf-title">核对您的处方信息</h2>                                
                        <p class="cf-sub">确认前再看一遍，让每一项记录都准确。</p>
                    </div>
                    <button type="button" class="cf-close" aria-label="关闭" @click="closeConfirm">✕</button>
                </div>

                <div class="cf-body">
                    <div class="cf-main">
                        <!-- 患者信息 -->
                        <div class="cf-patient">
                            <span class="cf-avatar">{{ avatarText }}</span>
                            <div class="cf-pinfo">
                                <span class="cf-pname">{{ consultInfo.contact.name }}</span>
                                <span class="cf-pmeta">{{ sexText }}<span v-if="birthdayText"> · {{ birthdayText }} 出生</span><!-- 出生时间未知提示 暂时隐藏（保留代码，恢复时去掉注释即可）<span
                                        v-if="!hasBirthTime"> · 出生时间未知</span> --></span>
                            </div>
                            <span class="cf-badge">{{ isChaoweiFlow ? '调理方案' : '中药AI组方' }}</span>
                            <div class="cf-paddr">
                                <div class="cf-paddr-item" v-if="consultInfo.contact.liveArea">
                                    <span class="cf-k">常住地址</span>
                                    <span class="cf-v">{{ consultInfo.contact.liveArea }}</span>
                                </div>
                                <div class="cf-paddr-item" v-if="consultInfo.contact.birthArea">
                                    <span class="cf-k">出生地址</span>
                                    <span class="cf-v">{{ consultInfo.contact.birthArea }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- 就诊信息 -->
                        <div class="cf-section">
                            <div class="cf-sec-head">
                                <h3 class="cf-sec-title">就诊信息</h3>
                                <button type="button" class="cf-edit" @click="editStep(1)">修改</button>
                            </div>
                            <div class="cf-grid">
                                <div class="cf-item" v-for="item in reviewVisit" :key="item.label">
                                    <span class="cf-k">{{ item.label }}</span>
                                    <span class="cf-v">{{ item.value }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- 症状信息 -->
                        <div class="cf-section">
                            <div class="cf-sec-head">
                                <h3 class="cf-sec-title">{{ isChaoweiFlow ? '健康问询' : '症状信息' }}</h3>
                                <button type="button" class="cf-edit" @click="editStep(2)">修改</button>
                            </div>
                            <div class="cf-grid" v-if="reviewSymptoms.length > 0">
                                <div class="cf-item" v-for="item in reviewSymptoms" :key="item.label">
                                    <span class="cf-k">{{ item.label }}</span>
                                    <span class="cf-v">{{ item.value }}</span>
                                </div>
                            </div>
                            <p class="cf-empty" v-else>未填写</p>
                        </div>

                        <!-- 三焦症状 -->
                        <div class="cf-section">
                            <div class="cf-sec-head">
                                <h3 class="cf-sec-title">{{ isChaoweiFlow ? '三焦情况' : '三焦症状' }}
                                    <span class="cf-count">{{ selectedSanjiaoCount }}</span>
                                </h3>
                                <button type="button" class="cf-edit" @click="editStep(3)">修改</button>
                            </div>
                            <div class="cf-sanjiao">
                                <div class="cf-sj-item" v-for="r in reviewSanjiao" :key="r.key">
                                    <span class="cf-sj-name">{{ r.name }}</span>
                                    <span class="cf-sj-val" :class="{ 'is-empty': r.text === '未选择症状' }">{{ r.text }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 服务确认 -->
                    <aside class="cf-side">
                        <h3 class="cf-side-title">服务确认</h3>
                        <div class="cf-side-block">
                            <span class="cf-side-k">支付金额</span>
                            <span class="cf-amount">{{ payAmount }}<i>元</i></span>
                        </div>
                        <div class="cf-side-block">
                            <span class="cf-side-k">支付方式</span>
                            <el-radio-group v-model="payType.value" class="cf-pay-group">
                                <el-radio-button v-for="opt in payType.options" :key="opt" :label="opt">{{ opt }}
                                </el-radio-button>
                            </el-radio-group>
                        </div>
                        <div class="cf-side-block">
                            <span class="cf-side-k">服务状态</span>
                            <span class="cf-side-v">待提交</span>
                        </div>
                        <p class="cf-note">核对无误后点击「确认并保存」将进入支付流程；如需调整，请点击对应区块右上角的「修改」。</p>
                        <p class="cf-note">处方请遵专业医师审核，请勿私自填写内容进行购药。</p>
                    </aside>
                </div>

                <div class="cf-foot">
                    <el-checkbox v-model="agreed" class="cf-agree">我已核对以上信息，确认无误</el-checkbox>
                    <div class="cf-actions">
                        <button type="button" class="btn-prev" @click="closeConfirm">返回修改</button>
                        <button type="button" class="btn-submit" :disabled="!agreed || payLoading" @click="confirmSave">
                            确认并保存
                        </button>
                    </div>
                </div>
            </div>
        </el-dialog>

        <!-- 微信支付二维码弹窗 -->
        <el-dialog title="微信支付" :visible.sync="qrcodeVisble" width="360px" :close-on-click-modal="dialogClosable"
            class="qr-dialog">
            <div class="qr-wrapper">
                <div id="qrcode"></div>
                <span class="qr-text">二维码过期了？<b @click="qrcodeRefresh">立即刷新</b></span>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import form from '../../js/form'
import QRCode from 'qrcodejs2'

export default {
    name: 'PrescriptionInfoPage',
    props: {
        config: {
            type: Object,
            default: () => ({
                pageTitle: '处方信息',
                subtitle: '',
                useInfoTitle: '处方使用信息',
                showSymptoms: false,
                submitRouteMode1: '/prescriptionResult',
                submitRouteMode2: '/handmadePrescription',
                btnTextMode1: '提交处方信息',
                btnTextMode2: '请输入药方',
                showGanZhiLabel: false,
                supportMode: false,
                priceApi: '/api/health/v1/price',
                redirectRoute: '/Prescription',
            }),
        },
    },
    data() {
        return {
            consultInfo: {
                contact: {},
                useDate: '',
                useDays: '',
            },
            submitData: {},
            activeNames: '',
            radioDisable: false,
            mode: 1,
            currentStep: 1,
            activeSanjiao: 'shangjiao',
            // 核对弹框
            confirmVisible: false,
            agreed: false,
            // 支付
            payType: {
                key: '支付方式',
                value: '公共账户',
                options: ['公共账户', '微信支付'],
            },
            price: 0,
            freeFlag: false,
            payLoading: false,
            qrcodeVisble: false,
            outTradeNo: '',
            visitRecordId: sessionStorage.getItem('visitRecordId') || '',
            dialogClosable: false,
        }
    },
    computed: {
        submitBtnText() {
            if (this.config.supportMode) {
                return this.mode == 1 ? this.config.btnTextMode1 : this.config.btnTextMode2
            }
            return this.config.btnTextMode1
        },
        isChaoweiFlow() {
            return this.$route.path.indexOf('chaowei') !== -1
        },
        effectiveUseInfoTitle() {
            if (this.isChaoweiFlow) return '使用信息'
            return this.config.useInfoTitle
        },
        hasUseInfo() {
            return !!(this.consultInfo.useDate || this.consultInfo.ganZhiPrescriptionHourRange)
        },
        useDaysLabel() {
            if (this.isChaoweiFlow) {
                return this.consultInfo.ganZhiPrescriptionHourRange ? '服用时段' : '调理天数'
            }
            if (!this.config.showGanZhiLabel) return '服药天数'
            return this.consultInfo.ganZhiPrescriptionHourRange ? '服用时间段' : '服药天数'
        },
        useDaysValue() {
            if (this.isChaoweiFlow) {
                return this.consultInfo.ganZhiPrescriptionHourRange || this.consultInfo.useDays
            }
            if (this.consultInfo.ganZhiPrescriptionHourRange) return this.consultInfo.ganZhiPrescriptionHourRange
            return this.consultInfo.useDays
        },
        totalSteps() {
            return this.config.showSymptoms ? 3 : 1
        },
        stepList() {
            var chaowei = this.isChaoweiFlow
            var list = [{
                idx: 1,
                name: '就诊信息',
                desc: chaowei ? '调理时间与就诊描述' : '服药时间与就诊描述'
            }]
            if (this.config.showSymptoms) {
                list.push({ idx: 2, name: chaowei ? '健康问询' : '症状信息', desc: '体征、生活习惯与脉象' })
                list.push({ idx: 3, name: chaowei ? '三焦情况' : '三焦症状', desc: '按部位选择实际症状' })
            }
            return list
        },
        currentStepName() {
            return this.stepList[this.currentStep - 1] ? this.stepList[this.currentStep - 1].name : ''
        },
        currentStepDesc() {
            if (this.currentStep === 1) return '记录本次就诊时间与主要情况，帮助医生了解您的需求。'
            if (this.currentStep === 2) return '填写实际数据，并选择生活状态与脉象；不了解的项目可不填。'
            return '按身体部位浏览并选择已有症状，可多选。仅记录实际情况，不建议自行判断疾病。'
        },
        avatarText() {
            var n = this.consultInfo.contact.name
            return n && n.length > 0 ? n.charAt(0) : '患'
        },
        sexText() {
            if (this.isEmptyValue(this.consultInfo.contact.sex)) return ''
            return this.consultInfo.contact.sex == '1' ? '男' : '女'
        },
        birthdayText() {
            var c = this.consultInfo.contact
            if (this.isEmptyValue(c.birthday)) return ''
            var text = c.birthday
            // 出生时分 暂时隐藏（保留代码，恢复时去掉注释即可）
            // if (this.shouldShowTime(c.birthHour, c.birthMinute)) {
            //     text += ' ' + this.formatTimeValue(c.birthHour) + ':' + this.formatTimeValue(c.birthMinute)
            // }
            return text
        },
        sanjiaoRegions() {
            return [
                { key: 'shangjiao', name: '上焦', desc: '头面、胸廓与心肺' },
                { key: 'zhongjiao', name: '中焦', desc: '脾胃、肝胆与胰腺' },
                { key: 'xiajiao', name: '下焦', desc: '肾膀胱、腰腿与生殖' },
            ]
        },
        activeSanjiaoGroups() {
            if (this.activeSanjiao === 'zhongjiao') return this.submitData.zhongjiao_array || []
            if (this.activeSanjiao === 'xiajiao') return this.submitData.xiajiao_array || []
            return this.submitData.shangjiao_array || []
        },
        selectedSanjiaoCount() {
            var total = 0
            var groups = (this.submitData.shangjiao_array || [])
                .concat(this.submitData.zhongjiao_array || [])
                .concat(this.submitData.xiajiao_array || [])
            groups.forEach((g) => {
                if (Array.isArray(g.value)) total += g.value.length
            })
            return total
        },
        payAmount() {
            return this.payType.value == '公共账户' && this.freeFlag == 'Y' ? 0 : this.price
        },
        hasBirthTime() {
            // 出生时分 暂时隐藏（保留代码，恢复时去掉注释即可）
            // var c = this.consultInfo.contact
            // return this.shouldShowTime(c.birthHour, c.birthMinute)
        },
        reviewVisit() {
            var arr = []
            if (this.consultInfo.useDate) {
                arr.push({ label: this.isChaoweiFlow ? '开始调理' : '开始服药', value: this.consultInfo.useDate })
            }
            if (this.config.showGanZhiLabel && this.consultInfo.illDay) {
                arr.push({ label: '发病日期', value: this.consultInfo.illDay })
            }
            if (this.useDaysValue) {
                arr.push({ label: this.useDaysLabel, value: this.useDaysValue })
            }
            var ba = this.submitData.basic_array || []
            if (ba.length > 0) {
                arr.push({ label: ba[0].key, value: ba[0].value })
            }
            var be = this.submitData.basic_enter_array || []
            if (be.length > 4 && String(be[4].value).length > 0) {
                arr.push({ label: this.isChaoweiFlow ? '问询结果' : be[4].key, value: be[4].value })
            }
            if (!this.isChaoweiFlow && be.length > 5 && String(be[5].value).length > 0) {
                arr.push({ label: be[5].key, value: be[5].value })
            }
            return arr
        },
        reviewSymptoms() {
            var arr = []
            var units = ['℃', '次/分', 'mmHg', 'mmHg']
            var be = this.submitData.basic_enter_array || []
            for (var i = 0; i < 4; i++) {
                if (be[i] && String(be[i].value).length > 0) {
                    arr.push({ label: be[i].key, value: be[i].value + ' ' + units[i] })
                }
            }
            var ba = this.submitData.basic_array || []
            for (var j = 1; j < ba.length; j++) {
                if (ba[j].value != '正常') {
                    arr.push({ label: ba[j].key, value: ba[j].value })
                }
            }
            var mx = this.submitData.maixiang_array || []
            for (var k = 0; k < mx.length; k++) {
                if (mx[k].value != '正常') {
                    arr.push({ label: mx[k].key, value: mx[k].value })
                }
            }
            return arr
        },
        reviewSanjiao() {
            var map = {
                shangjiao: this.submitData.shangjiao_array || [],
                zhongjiao: this.submitData.zhongjiao_array || [],
                xiajiao: this.submitData.xiajiao_array || [],
            }
            return this.sanjiaoRegions.map((r) => {
                var texts = []
                ;(map[r.key] || []).forEach((g) => {
                    if (Array.isArray(g.value) && g.value.length > 0) {
                        texts.push(g.key + '：' + g.value.join('、'))
                    }
                })
                return { key: r.key, name: r.name, text: texts.length > 0 ? texts.join('；') : '未选择症状' }
            })
        },
    },
    created() {
        if (this.config.supportMode && this.$route.query.mode) {
            this.mode = Number(this.$route.query.mode)
        }
        // 检查缓存是否存在/有效，缺失则跳转首页
        var consultStr = sessionStorage.getItem('consultInfo')
        var consult = null
        if (this.utils.isEmptyStr(consultStr)) {
            this.$router.replace('/')
            return
        }
        try {
            consult = JSON.parse(consultStr)
        } catch (e) {
            this.$router.replace('/')
            return
        }
        // 如果 contact 字段缺失或为空对象，说明缓存已被破坏（订单提交后被清空），跳转首页
        if (this.utils.isEmptyObject(consult) || this.utils.isEmptyObject(consult.contact)) {
            this.$router.replace('/')
            return
        }
        var submitData = sessionStorage.getItem('submitData')
        if (this.utils.isEmptyStr(submitData)) {
            for (var i = 0; i < form.basic_array.length; i++) {
                form.basic_array[i].value = i == 0 ? '否' : '正常'
            }
            for (var i = 0; i < form.basic_enter_array.length; i++) {
                form.basic_enter_array[i].value = ''
            }
            for (var i = 0; i < form.maixiang_array.length; i++) {
                form.maixiang_array[i].value = '正常'
            }
            for (var i = 0; i < form.shangjiao_array.length; i++) {
                form.shangjiao_array[i].value = []
            }
            for (var i = 0; i < form.zhongjiao_array.length; i++) {
                form.zhongjiao_array[i].value = []
            }
            for (var i = 0; i < form.xiajiao_array.length; i++) {
                form.xiajiao_array[i].value = []
            }
            this.submitData = form
        } else {
            this.submitData = JSON.parse(submitData)
        }
        // 兼容历史/异常数据：三焦 value 若为逗号字符串则统一转为数组，避免选择异常
        this.normalizeSanjiao(this.submitData.shangjiao_array)
        this.normalizeSanjiao(this.submitData.zhongjiao_array)
        this.normalizeSanjiao(this.submitData.xiajiao_array)
        var consult = JSON.parse(consultStr)
        if (!this.utils.isEmptyObject(consult) && !this.utils.isEmptyObject(consult.contact)) {
            this.consultInfo = consult
            this.radioDisable = consult.contact.sex == '1'
        } else {
            this.$router.replace('/')
        }
        // 已登录（有用户信息）时仅保留公共账户支付
        var userInfo = this.$store.getters.getUserInfo
        if (!this.utils.isEmptyObject(userInfo)) {
            this.payType.options = ['公共账户']
            this.payType.value = '公共账户'
        }
    },
    watch: {
        qrcodeVisble: function (val) {
            if (!val && this.mInterval != undefined && this.mInterval != null) {
                clearInterval(this.mInterval)
            }
        },
    },
    methods: {
        chaoweiFieldLabel(idx) {
            if (!this.isChaoweiFlow) return null
            if (idx === 4 || idx === 5) return '问询结果'
            return null
        },
        shouldShowTime(hour, minute) {
            return !(this.isEmptyValue(hour) && this.isEmptyValue(minute))
        },
        isEmptyValue(value) {
            return value === '' || value === null || value === undefined
        },
        formatTimeValue(value) {
            if (value === '-1' || value === -1 || value === '--') {
                return '--'
            }
            return value
        },
        pad(n) {
            return n < 10 ? '0' + n : '' + n
        },
        goHome() {
            this.$router.replace('/')
        },
        goBack() {
            this.$router.go(-1)
        },
        goStep(n) {
            if (n >= 1 && n <= this.totalSteps) {
                this.currentStep = n
            }
        },
        nextStep() {
            if (this.currentStep < this.totalSteps) {
                this.currentStep++
            }
        },
        prevStep() {
            if (this.currentStep > 1) {
                this.currentStep--
            }
        },
        normalizeSanjiao(arr) {
            (arr || []).forEach((g) => {
                if (Array.isArray(g.value)) return
                if (typeof g.value === 'string' && g.value.length > 0) {
                    this.$set(g, 'value', g.value.split(','))
                } else {
                    this.$set(g, 'value', [])
                }
            })
        },
        clearSanjiao() {
            var all = (this.submitData.shangjiao_array || [])
                .concat(this.submitData.zhongjiao_array || [])
                .concat(this.submitData.xiajiao_array || [])
            all.forEach((g) => {
                this.$set(g, 'value', [])
            })
        },
        openConfirm() {
            this.agreed = false
            this.confirmVisible = true
            this.getPayAmount()
        },
        // 关闭弹框（未支付）：清除已生成的记录 ID，确保下次提交按最新选择重新生成记录
        closeConfirm() {
            this.confirmVisible = false
            sessionStorage.removeItem('visitRecordId')
            this.visitRecordId = ''
        },
        editStep(n) {
            this.closeConfirm()
            this.goStep(n)
        },
        confirmSave() {
            if (!this.agreed) {
                this.$message({ message: '请先勾选“我已核对信息”', type: 'warning' })
                return
            }
            this.pay(false)
        },
        buildPayParams() {
            // 注意：不能用返回值覆盖原数组（会破坏页面表单数据），需浅拷贝
            var toStrArray = (arr) => (arr || []).map((obj) => {
                var val = Array.isArray(obj.value) ? obj.value.toString() : obj.value
                return Object.assign({}, obj, { value: val })
            })
            var newSubmitData = {
                basic_array: this.submitData.basic_array,
                basic_enter_array: this.submitData.basic_enter_array,
                maixiang_array: this.submitData.maixiang_array,
                shangjiao_array: toStrArray(this.submitData.shangjiao_array),
                zhongjiao_array: toStrArray(this.submitData.zhongjiao_array),
                xiajiao_array: toStrArray(this.submitData.xiajiao_array),
            }
            var params = {
                patientId: this.consultInfo.contact.patientId,
                toDoDay: this.consultInfo.useDate,
                takeDays: this.consultInfo.useDays,
                visitRecordId: sessionStorage.getItem('visitRecordId'),
            }
            if (this.config.payGanZhiLabel !== false) {
                params.illDay = this.consultInfo.illDay
                params.ganZhiPrescriptionTimeType = this.consultInfo.ganZhiPrescriptionTimeType
                params.takeHours = this.consultInfo.ganZhiPrescriptionHourRange
                params.inquiryInfo = newSubmitData
            }
            if (this.config.recordType) {
                params.recordType = this.config.recordType
            }
            return params
        },
        getPayAmount() {
            var params = this.buildPayParams()
            this.wjtHttp.post(this, this.config.priceApi, params, false, (res) => {
                var data = res.data
                if (data.code == 200) {
                    this.price = data.data.price
                    this.freeFlag = data.data.freeFlag
                    this.visitRecordId = data.data.visitRecordId
                    sessionStorage.setItem('visitRecordId', this.visitRecordId)
                } else {
                    this.$message({
                        message: data.msg && data.msg.length > 0 ? data.msg : '获取支付金额异常，请联系管理员',
                        type: 'error',
                    })
                }
            }, (err) => {
                console.error(err)
            })
        },
        pay(isRefresh) {
            var params = {
                productId: this.visitRecordId,
                orderType: 1,
                tradeType: 'NATIVE',
                payChannel: this.payType.value == '微信支付' ? '1' : '3',
            }
            if (!isRefresh) {
                this.payLoading = true
            }
            this.wjtHttp.post(this, '/api/api/pay/0.1/unifiedOrder', params, false, (res) => {
                var data = res.data
                this.payLoading = false
                if (data.code == 200) {
                    if (data.data.payChannel == 1 && !this.utils.isEmptyStr(data.data.qrcode)) {
                        this.qrcodeVisble = true
                        this.outTradeNo = data.data.outTradeNo
                        this.$nextTick(() => {
                            if (this.mQrCode != undefined && this.mQrCode != null) {
                                this.mQrCode.clear()
                                this.mQrCode.makeCode(data.data.qrcode)
                            } else {
                                this.mQrCode = new QRCode('qrcode', {
                                    text: data.data.qrcode,
                                    width: 256,
                                    height: 256,
                                    colorDark: '#000000',
                                    colorLight: '#ffffff',
                                    correctLevel: QRCode.CorrectLevel.H,
                                })
                            }
                            this.getPayStatus()
                        })
                    } else {
                        this.$message({ message: '支付成功', type: 'success' })
                        setTimeout(() => {
                            this.redirectAfterPay()
                        }, 1000)
                    }
                } else {
                    this.$message({
                        message: !this.utils.isEmptyStr(data.msg) ? data.msg : '支付异常，请联系管理员',
                        type: 'error',
                    })
                }
            }, (err) => {
                console.error(err)
                this.payLoading = false
            })
        },
        qrcodeRefresh() {
            this.pay(true)
        },
        getPayStatus() {
            if (this.mInterval != undefined && this.mInterval != null) {
                clearInterval(this.mInterval)
            }
            this.mInterval = setInterval(() => {
                var params = {
                    outTradeNo: this.outTradeNo,
                }
                this.wjtHttp.post(this, '/api/api/pay/0.1/query/place/result', params, false, (res) => {
                    var data = res.data
                    if (data.code == 200) {
                        if (data.data == 1103) {
                            this.$message({ message: '支付成功', type: 'success' })
                            this.qrcodeVisble = false
                            setTimeout(() => {
                                this.redirectAfterPay()
                            }, 1000)
                            clearInterval(this.mInterval)
                        }
                    }
                }, (err) => {
                    console.error(err)
                })
            }, 3000)
        },
        redirectAfterPay() {
            this.$router.push({
                path: this.config.redirectRoute,
                query: {
                    visitRecordId: this.visitRecordId,
                    patientId: this.consultInfo.contact.patientId,
                },
            })
        },
        submit() {
            sessionStorage.setItem('submitData', JSON.stringify(this.submitData))
            if (this.config.supportMode && this.mode == 2) {
                this.$router.push(this.config.submitRouteMode2)
                return
            }
            this.openConfirm()
        },
    },
}
</script>

<style scoped>
.page-wrapper {
    min-height: 100vh;
    background: #f5f7f2;
    font-family: '思源黑体', 'Source Han Sans SC', 'Noto Sans SC', system-ui, sans-serif;
    color: #304744;
    padding-bottom: 40px;
}

.page-container {
    max-width: 1220px;
    margin: 0 auto;
    padding: 32px 24px 40px;
}

/* 面包屑 */
.breadcrumb {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    margin-bottom: 18px;
}

.bc-link {
    color: #527f77;
    cursor: pointer;
}

.bc-link:hover {
    color: #009798;
}

.bc-sep {
    color: #b6c6c1;
}

.bc-current {
    color: #304744;
    font-weight: 600;
}

/* Hero */
.hero-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 26px;
}

.hero-brand {
    margin: 0 0 14px;
    font-size: 16px;
    font-weight: 600;
    color: #009798;
    letter-spacing: 1px;
}

.hero-title {
    margin: 0 0 12px;
    font-size: 40px;
    font-weight: 700;
    color: #304744;
    letter-spacing: 1px;
    line-height: 1.2;
}

.hero-subtitle {
    margin: 0;
    font-size: 17px;
    color: #527f77;
}

.hero-back {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 46px;
    padding: 0 22px;
    border: 1px solid #cfdcd8;
    border-radius: 12px;
    background: #fff;
    color: #304744;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.hero-back:hover {
    border-color: #009798;
    color: #009798;
}

.hero-back-arrow {
    color: #009798;
    font-weight: 700;
}

/* 双栏布局 */
.layout {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 28px;
    align-items: start;
}

/* 左侧 */
.side-col {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.patient-card {
    background: #fff;
    border: 1px solid #e9efec;
    border-radius: 20px;
    padding: 24px 22px;
    box-shadow: 0 10px 30px rgba(23, 61, 59, 0.05);
}

.side-eyebrow {
    margin: 0 0 18px;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 2px;
    color: #a9bcb6;
}

.patient-head {
    display: flex;
    align-items: center;
    gap: 14px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eef2f0;
    margin-bottom: 16px;
}

.patient-avatar {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #d9efeb;
    color: #009798;
    font-size: 22px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.patient-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.patient-name {
    font-size: 22px;
    font-weight: 700;
    color: #1f3a34;
}

.patient-sub {
    font-size: 16px;
    color: #7f9994;
}

.patient-rows {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.p-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.p-k {
    font-size: 16px;
    color: #9aa8a3;
}

.p-v {
    font-size: 16px;
    color: #304744;
}

/* 步骤选项卡 */
.steps {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.step-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border: 1px solid transparent;
    border-radius: 14px;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: all 0.2s ease;
    font-family: inherit;
}

.step-item:hover {
    background: #eef6f4;
}

.step-item.active {
    background: #e6f4f1;
    border-color: #cfe5e2;
}

.step-idx {
    font-size: 16px;
    font-weight: 700;
    color: #009798;
    flex-shrink: 0;
}

.step-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
}

.step-name {
    font-size: 17px;
    font-weight: 600;
    color: #304744;
}

.step-desc {
    font-size: 16px;
    color: #9aa8a3;
}

.step-arrow {
    color: #9aa8a3;
    font-size: 16px;
    flex-shrink: 0;
}

.step-item.active .step-arrow {
    color: #009798;
}

.side-hint {
    margin: 0;
    font-size: 16px;
    color: #9aa8a3;
    line-height: 1.7;
}

/* 右侧表单卡 */
.form-card {
    background: #fff;
    border: 1px solid #e9efec;
    border-radius: 22px;
    padding: 30px 40px 24px;
    box-shadow: 0 10px 30px rgba(23, 61, 59, 0.06);
}

.form-head {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 18px;
    border-bottom: 1px solid #eef2f0;
}

.form-eyebrow {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 2px;
    color: #a9bcb6;
}

.form-head-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.form-title {
    margin: 0;
    font-size: 30px;
    font-weight: 700;
    color: #304744;
}

.form-badge {
    flex-shrink: 0;
    padding: 6px 16px;
    border-radius: 10px;
    background: #e6f4f1;
    color: #009798;
    font-size: 16px;
    font-weight: 600;
}

.form-sub {
    margin: 18px 0 4px;
    font-size: 16px;
    color: #7f9994;
    line-height: 1.7;
}

/* 表单字段 */
.step-body {
    padding-top: 12px;
}

.grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px 28px;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
}

.field-full {
    grid-column: 1 / -1;
}

.field-standalone {
    margin-bottom: 20px;
}

.textarea-grid {
    margin-top: 18px;
}

.f-label {
    font-size: 16px;
    font-weight: 600;
    color: #304744;
}

.f-readonly {
    display: flex;
    align-items: center;
    height: 48px;
    padding: 0 16px;
    border: 1px solid #d3e6e3;
    border-radius: 12px;
    background: #fbfdfd;
    color: #304744;
    font-size: 16px;
}

.input-wrap {
    position: relative;
    display: flex;
    align-items: center;
}

.unit {
    position: absolute;
    right: 16px;
    font-size: 16px;
    color: #9aa8a3;
    pointer-events: none;
}

.input-wrap>>>.el-input__inner {
    padding-right: 60px;
}

.sub-title {
    margin: 12px 0 18px;
    font-size: 18px;
    font-weight: 600;
    color: #304744;
}

/* el 输入框 */
.field>>>.el-input__inner,
.field>>>.el-textarea__inner {
    border: 1px solid #d3e6e3;
    border-radius: 12px;
    background: #fbfdfd;
    font-size: 16px;
    color: #304744;
}

.field>>>.el-input__inner {
    height: 48px;
    line-height: 48px;
}

.field>>>.el-textarea__inner {
    line-height: 1.7;
    padding: 12px 16px;
}

.field>>>.el-input__inner:focus,
.field>>>.el-textarea__inner:focus {
    border-color: #009798;
}

.field>>>.el-input__inner::placeholder,
.field>>>.el-textarea__inner::placeholder {
    color: #a7b8b4;
}

/* 单选胶囊 */
.pill-group>>>.el-radio-button__inner {
    border: 1px solid #dbe6e3;
    border-radius: 8px !important;
    background: #fff;
    color: #527f77;
    font-size: 16px;
    padding: 9px 22px;
    line-height: 1.4;
    box-shadow: none !important;
}

.pill-group>>>.el-radio-button {
    margin: 0 10px 0 0;
}

.pill-group>>>.el-radio-button:last-child {
    margin-right: 0;
}

.pill-group>>>.el-radio-button__orig-radio:checked+.el-radio-button__inner {
    background: #009798;
    border-color: #009798;
    color: #fff;
}

.pill-group>>>.el-radio-button__inner:hover {
    color: #009798;
}

.pill-group.is-disabled>>>.el-radio-button__inner,
.pill-group>>>.el-radio-button__inner.is-disabled {
    opacity: 0.7;
}

/* 三焦区域选择 */
.sanjiao-tabs {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    margin-bottom: 22px;
}

.sanjiao-tab {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 18px 20px;
    border: 1px solid #dbe6e3;
    border-radius: 14px;
    background: #fff;
    cursor: pointer;
    text-align: left;
    transition: all 0.2s ease;
    font-family: inherit;
}

.sanjiao-tab:hover {
    border-color: #009798;
}

.sanjiao-tab.active {
    border-color: #009798;
    background: #e6f4f1;
}

.sj-name {
    font-size: 20px;
    font-weight: 700;
    color: #304744;
}

.sanjiao-tab.active .sj-name {
    color: #006f70;
}

.sj-desc {
    font-size: 16px;
    color: #9aa8a3;
}

.sanjiao-tab.active .sj-desc {
    color: #2f6d64;
}

/* 症状分组 */
.sanjiao-groups {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.sj-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sj-group-title {
    font-size: 17px;
    font-weight: 600;
    color: #009798;
}

/* 症状标签 */
.chip-group {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.chip-group>>>.el-checkbox-button__inner {
    border: 1px solid #dbe6e3;
    border-radius: 10px !important;
    background: #fff;
    color: #527f77;
    font-size: 16px;
    padding: 9px 18px;
    line-height: 1.4;
    box-shadow: none !important;
}

.chip-group>>>.el-checkbox-button {
    margin: 0;
}

.chip-group>>>.el-checkbox-button.is-checked .el-checkbox-button__inner {
    background: #e6f4f1;
    border-color: #009798;
    color: #006f70;
    font-weight: 600;
}

/* 已选统计 */
.sanjiao-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 22px;
    padding: 14px 20px;
    border-radius: 12px;
    background: #eef6f4;
}

.bar-count {
    font-size: 16px;
    color: #527f77;
}

.bar-clear {
    border: none;
    background: none;
    color: #009798;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
}

.bar-clear:hover {
    text-decoration: underline;
}

/* 底部 */
.form-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 26px;
    padding-top: 20px;
    border-top: 1px solid #eef2f0;
}

.form-hint {
    font-size: 16px;
    color: #9aa8a3;
}

.form-nav {
    display: flex;
    align-items: center;
    gap: 12px;
}

.btn-prev {
    height: 46px;
    padding: 0 24px;
    border: 1px solid #cfdcd8;
    border-radius: 12px;
    background: #fff;
    color: #304744;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
}

.btn-prev:hover {
    border-color: #009798;
    color: #009798;
}

.btn-next,
.btn-submit {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 46px;
    padding: 0 28px;
    border: none;
    border-radius: 12px;
    background: #009798;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 10px 22px rgba(0, 151, 152, 0.22);
    transition: all 0.2s ease;
    font-family: inherit;
}

.btn-next:hover,
.btn-submit:hover {
    background: #007e7f;
    box-shadow: 0 12px 26px rgba(0, 151, 152, 0.3);
}

.nav-arrow {
    font-weight: 700;
}

/* 响应式 */
@media (max-width: 1024px) {
    .layout {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .page-container {
        padding: 20px 16px 32px;
    }

    .hero-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }

    .hero-title {
        font-size: 28px;
    }

    .form-card {
        padding: 22px 18px 20px;
    }

    .grid-2 {
        grid-template-columns: 1fr;
    }

    .sanjiao-tabs {
        grid-template-columns: 1fr;
    }

    .form-footer {
        flex-direction: column;
        align-items: flex-start;
    }

    .cf-dialog>>>.el-dialog {
        width: 92% !important;
    }

    .cf-body {
        grid-template-columns: 1fr;
    }

    .cf-grid {
        grid-template-columns: 1fr;
    }

    .cf-paddr {
        gap: 20px;
    }

    .cf-foot {
        flex-direction: column;
        align-items: flex-start;
    }
}

/* ===== 核对确认弹框 ===== */
.cf-dialog>>>.el-dialog {
    border-radius: 20px;
    overflow: hidden;
    margin-top: 6vh !important;
}

.cf-dialog>>>.el-dialog__header {
    display: none;
}

.cf-dialog>>>.el-dialog__body {
    padding: 0;
}

.cf {
    display: flex;
    flex-direction: column;
    max-height: 84vh;
}

.cf-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 26px 34px 18px;
    border-bottom: 1px solid #eef2f0;
}

.cf-eyebrow {
    margin: 0 0 10px;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 2px;
    color: #a9bcb6;
}

.cf-title {
    margin: 0 0 8px;
    font-size: 28px;
    font-weight: 700;
    color: #304744;
}

.cf-sub {
    margin: 0;
    font-size: 16px;
    color: #7f9994;
}

.cf-close {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border: 1px solid #dbe6e3;
    border-radius: 50%;
    background: #fff;
    color: #527f77;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
}

.cf-close:hover {
    border-color: #009798;
    color: #009798;
}

.cf-body {
    display: grid;
    grid-template-columns: 1fr 290px;
    gap: 24px;
    padding: 22px 34px;
    overflow-y: auto;
}

.cf-main {
    display: flex;
    flex-direction: column;
    gap: 22px;
    min-width: 0;
}

/* 患者信息 */
.cf-patient {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    border-radius: 16px;
    background: #f2f8f6;
    flex-wrap: wrap;
}

.cf-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #cfe8e4;
    color: #009798;
    font-size: 20px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.cf-pinfo {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
}

.cf-pname {
    font-size: 20px;
    font-weight: 700;
    color: #1f3a34;
}

.cf-pmeta {
    font-size: 16px;
    color: #7f9994;
}

.cf-badge {
    flex-shrink: 0;
    padding: 6px 14px;
    border-radius: 999px;
    background: #e6f4f1;
    color: #009798;
    font-size: 16px;
    font-weight: 600;
}

.cf-paddr {
    width: 100%;
    display: flex;
    gap: 40px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #cfe5e2;
}

.cf-paddr-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

/* 区块 */
.cf-section {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.cf-sec-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eef2f0;
}

.cf-sec-title {
    margin: 0;
    font-size: 19px;
    font-weight: 700;
    color: #304744;
    display: flex;
    align-items: center;
    gap: 8px;
}

.cf-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 22px;
    padding: 0 8px;
    border-radius: 999px;
    background: #e6f4f1;
    color: #009798;
    font-size: 16px;
    font-weight: 600;
}

.cf-edit {
    border: none;
    background: none;
    color: #009798;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
}

.cf-edit:hover {
    text-decoration: underline;
}

.cf-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px 28px;
}

.cf-item {
    display: flex;
    align-items: baseline;
    gap: 12px;
    min-width: 0;
}

.cf-k {
    flex-shrink: 0;
    min-width: 76px;
    font-size: 16px;
    color: #9aa8a3;
}

.cf-v {
    font-size: 16px;
    color: #304744;
    word-break: break-all;
}

.cf-empty {
    margin: 0;
    font-size: 16px;
    color: #b6c6c1;
}

/* 三焦 */
.cf-sanjiao {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.cf-sj-item {
    display: flex;
    align-items: baseline;
    gap: 16px;
    padding: 12px 16px;
    border: 1px solid #e9efec;
    border-radius: 12px;
}

.cf-sj-name {
    flex-shrink: 0;
    min-width: 48px;
    font-size: 16px;
    font-weight: 600;
    color: #009798;
}

.cf-sj-val {
    font-size: 16px;
    color: #304744;
    word-break: break-all;
}

.cf-sj-val.is-empty {
    color: #b6c6c1;
}

/* 服务确认 */
.cf-side {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 20px;
    border-radius: 16px;
    background: #f2f8f6;
    align-self: start;
}

.cf-side-eyebrow {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 2px;
    color: #a9bcb6;
}

.cf-side-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #006f70;
}

.cf-side-block {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 14px;
    border-bottom: 1px solid #dcebe8;
}

.cf-side-k {
    font-size: 16px;
    color: #7f9994;
}

.cf-amount {
    font-size: 30px;
    font-weight: 700;
    color: #009798;
    line-height: 1;
}

.cf-amount i {
    font-style: normal;
    font-size: 16px;
    font-weight: 600;
    margin-left: 4px;
}

.cf-side-v {
    font-size: 17px;
    font-weight: 600;
    color: #304744;
}

.cf-pay-group {
    display: flex;
    gap: 10px;
}

.cf-pay-group>>>.el-radio-button__inner {
    border: 1px solid #dbe6e3;
    border-radius: 8px !important;
    background: #fff;
    color: #527f77;
    font-size: 16px;
    padding: 8px 16px;
    box-shadow: none !important;
}

.cf-pay-group>>>.el-radio-button {
    margin: 0;
}

.cf-pay-group>>>.el-radio-button__orig-radio:checked+.el-radio-button__inner {
    background: #009798;
    border-color: #009798;
    color: #fff;
}

.cf-note {
    margin: 0;
    font-size: 16px;
    color: #9aa8a3;
    line-height: 1.7;
}

/* 弹框底部 */
.cf-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 34px 22px;
    border-top: 1px solid #eef2f0;
    flex-wrap: wrap;
}

.cf-agree>>>.el-checkbox__label {
    font-size: 16px;
    color: #527f77;
}

.cf-agree>>>.el-checkbox__input.is-checked .el-checkbox__inner {
    background-color: #009798;
    border-color: #009798;
}

.cf-agree>>>.el-checkbox__inner:hover {
    border-color: #009798;
}

.cf-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.cf-actions .btn-prev {
    height: 46px;
    padding: 0 24px;
    border: 1px solid #cfdcd8;
    border-radius: 12px;
    background: #fff;
    color: #304744;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
}

.cf-actions .btn-prev:hover {
    border-color: #009798;
    color: #009798;
}

.cf-actions .btn-submit {
    height: 46px;
    padding: 0 28px;
    border: none;
    border-radius: 12px;
    background: #009798;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 10px 22px rgba(0, 151, 152, 0.22);
    transition: all 0.2s ease;
    font-family: inherit;
}

.cf-actions .btn-submit:hover:not(:disabled) {
    background: #007e7f;
}

.cf-actions .btn-submit:disabled {
    background: #9fd0ce;
    box-shadow: none;
    cursor: not-allowed;
}

/* 支付二维码弹窗 */
.qr-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 0;
}

#qrcode {
    display: flex;
    justify-content: center;
}

#qrcode img {
    border-radius: 8px;
}

.qr-text {
    color: #333;
    font-size: 16px;
    margin-top: 24px;
}

.qr-text>b {
    color: #009798;
    font-size: 16px;
    cursor: pointer;
}
</style>
